import { useEffect, useState } from "react";

import type { StartedFeatureName } from "@/api/splashpages";
import { useStartedFeatures } from "@/hooks/useHasEngagedWithFeature";

export const DEFAULT_NEW_BADGE_TTL_DAYS = 30;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const NEW_BADGE_DISMISSED_PREFIX = "newBadgeDismissed:";

function isWithinTtl(newSince: Date, ttlDays: number): boolean {
  const since = newSince.getTime();
  if (Number.isNaN(since)) return false;
  return Date.now() < since + ttlDays * MS_PER_DAY;
}

function dismissedStorageKey(storageKey: string): string {
  return `${NEW_BADGE_DISMISSED_PREFIX}${storageKey}`;
}

// Same-tab localStorage writes don't emit a `storage` event, so an in-process
// subscriber set drives the immediate re-render after dismissal.
const dismissalSubscribers = new Set<() => void>();

function subscribeNewBadgeDismissal(callback: () => void): () => void {
  dismissalSubscribers.add(callback);
  return () => {
    dismissalSubscribers.delete(callback);
  };
}

function isNewBadgeDismissed(storageKey: string | undefined): boolean {
  if (!storageKey) return false;
  try {
    return localStorage.getItem(dismissedStorageKey(storageKey)) === "true";
  } catch {
    return false;
  }
}

// Persists the dismissal of a badge that has no `featureKey`, keyed by a stable
// identifier (e.g. a nav item's href or a settings page path), and notifies
// subscribers so the badge clears immediately without a reload.
export function dismissNewBadge(storageKey: string): void {
  try {
    localStorage.setItem(dismissedStorageKey(storageKey), "true");
  } catch {
    // Private mode or quota exceeded; the badge just won't persist its
    // dismissal across reloads.
  }
  dismissalSubscribers.forEach((notify) => notify());
}

// Controls visibility of a "New" badge. The badge shows only while both
// expiration gates are open:
//   1. Time gate: the TTL window since `newSince` (a UTC `Date`) has not
//      elapsed.
//   2. Interaction gate:
//      - With a `featureKey`: the user has not yet clicked "Get Started" on the
//        feature, tracked in the `started_features` table (the same record the
//        splash pages write). Because that query is updated optimistically on
//        click, the badge disappears immediately, without a page reload.
//      - Without a `featureKey`: the user has not dismissed the badge (e.g. by
//        clicking its nav item or visiting its page), tracked in localStorage
//        under `storageKey`.
// Returns false as soon as either gate closes. A badge with no `featureKey` and
// no `storageKey` relies on the time gate alone.
export function useNewBadge(
  featureKey: StartedFeatureName | undefined,
  newSince: Date | undefined,
  ttlDays: number = DEFAULT_NEW_BADGE_TTL_DAYS,
  storageKey?: string,
): boolean {
  const { startedFeatures, loading } = useStartedFeatures();
  const [dismissed, setDismissed] = useState(() =>
    isNewBadgeDismissed(storageKey),
  );

  useEffect(() => {
    setDismissed(isNewBadgeDismissed(storageKey));
    return subscribeNewBadgeDismissal(() => {
      setDismissed(isNewBadgeDismissed(storageKey));
    });
  }, [storageKey]);

  if (!newSince) return false;
  if (!isWithinTtl(newSince, ttlDays)) return false;

  if (featureKey) {
    // Hide until engagement state is known so an already-engaged user never
    // sees the badge flash in and then out.
    if (loading) return false;
    return !startedFeatures.has(featureKey);
  }

  return !dismissed;
}
